import { useState, useEffect, useCallback, useRef } from 'react'
import { collection, query, where, orderBy, onSnapshot, doc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore'
import { db } from '../config/firebase'

/**
 * Real-time messaging hook using Firestore onSnapshot
 * Provides live message updates, typing indicators, and read receipts
 */
export function useRealtimeMessages(clientId, tenantId) {
  const [realtimeMessages, setRealtimeMessages] = useState([])
  const [typingUsers, setTypingUsers] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const typingTimeoutRef = useRef(null)

  // Listen for new messages in real-time
  useEffect(() => {
    if (!clientId || !tenantId) return

    const messagesRef = collection(db, 'client_messages')
    const q = query(
      messagesRef,
      where('tenantId', '==', tenantId),
      where('clientId', '==', clientId),
      orderBy('createdAt', 'desc')
    )

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const msgs = []
      let unread = 0

      snapshot.forEach(doc => {
        const data = { id: doc.id, ...doc.data() }
        msgs.push(data)
        if (data.direction === 'outbound' && !data.isRead) {
          unread++
        }
      })

      setRealtimeMessages(msgs.reverse())
      setUnreadCount(unread)
    }, (error) => {
      console.warn('Realtime messages listener error:', error.message)
    })

    return () => unsubscribe()
  }, [clientId, tenantId])

  // Listen for typing indicators
  useEffect(() => {
    if (!clientId || !tenantId) return

    const typingRef = collection(db, 'typing_indicators')
    const q = query(
      typingRef,
      where('tenantId', '==', tenantId),
      where('clientId', '==', clientId),
      where('userType', '==', 'staff')
    )

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const typing = []
      snapshot.forEach(doc => {
        const data = doc.data()
        // Only show if typing started within last 10 seconds
        if (data.timestamp) {
          const ts = data.timestamp.toDate ? data.timestamp.toDate() : new Date(data.timestamp)
          if (Date.now() - ts.getTime() < 10000) {
            typing.push({ name: data.userName, userId: data.userId })
          }
        }
      })
      setTypingUsers(typing)
    }, () => {})

    return () => unsubscribe()
  }, [clientId, tenantId])

  // Set typing indicator for this client
  const setTyping = useCallback(async (isTyping) => {
    if (!clientId || !tenantId) return

    const typingDocId = `${tenantId}_${clientId}_client`
    const typingRef = doc(db, 'typing_indicators', typingDocId)

    if (isTyping) {
      try {
        await setDoc(typingRef, {
          tenantId,
          clientId,
          userId: clientId,
          userName: 'Client',
          userType: 'client',
          timestamp: serverTimestamp()
        })

        // Auto-clear after 5 seconds
        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current)
        typingTimeoutRef.current = setTimeout(async () => {
          try { await deleteDoc(typingRef) } catch { /* ignore */ }
        }, 5000)
      } catch { /* ignore */ }
    } else {
      try { await deleteDoc(typingRef) } catch { /* ignore */ }
    }
  }, [clientId, tenantId])

  // Mark messages as read
  const markAsRead = useCallback(async (messageIds) => {
    if (!messageIds?.length) return

    // We'll call the API endpoint for this since security rules
    // prevent direct client writes to client_messages
    // The realtime listener will pick up the change
  }, [])

  return {
    realtimeMessages,
    typingUsers,
    unreadCount,
    setTyping,
    markAsRead
  }
}

export default useRealtimeMessages
